import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { X, Download, ChevronLeft, ChevronRight, Loader2, Pin, PinOff } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { GALLERY_CATEGORIES } from "@/components/viral-gallery/types";

interface SequenceFrame {
  index: number;
  graphicId: string;
  r2Key: string;
  signedUrl: string;
  width: number;
  height: number;
}

interface SequenceDetail {
  id: string;
  title: string | null;
  frameCount: number;
  scriptJson: Record<string, unknown> | null;
  generationMeta: Record<string, unknown> | null;
  createdAt: string | null;
  conversationId: string | null;
  messageId: string | null;
  notes: string | null;
  isInGallery?: boolean | null;
  galleryCategory?: string | null;
  showInShowcase?: boolean | null;
}

interface Response {
  sequence: SequenceDetail;
  frames: SequenceFrame[];
}

interface Props {
  sequenceId: string;
  onClose: () => void;
}

export default function SequenceDetailModal({ sequenceId, onClose }: Props): JSX.Element {
  const [activeIdx, setActiveIdx] = useState(0);
  const { user } = useAuth();
  const isSuperAdmin = (user as { role?: string } | null)?.role === "super_admin";
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery<Response>({
    queryKey: ["stories-sequence", sequenceId],
    queryFn: () => apiRequest("GET", `/api/stories-creator/sequences/${sequenceId}`),
    staleTime: 60_000,
  });

  const pinMutation = useMutation({
    mutationFn: async (vars: { pin: boolean; category?: string }) =>
      apiRequest("POST", `/api/stories-creator/sequences/${sequenceId}/pin`, vars),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stories-sequence", sequenceId] });
      qc.invalidateQueries({ queryKey: ["stories-sequences"] });
      qc.invalidateQueries({ queryKey: ["gallery-sequences"] });
    },
  });

  // VP-101: simple owner toggle for the Stories-header showcase (no category prompt).
  const showcaseMutation = useMutation({
    mutationFn: async (show: boolean) =>
      apiRequest("POST", `/api/stories-creator/sequences/${sequenceId}/showcase`, { show }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stories-sequence", sequenceId] });
      qc.invalidateQueries({ queryKey: ["/api/stories-creator/sequences/showcase"] });
    },
  });

  const handlePinToggle = (): void => {
    const isPinned = data?.sequence.isInGallery === true;
    if (isPinned) {
      pinMutation.mutate({ pin: false });
      return;
    }
    // Ask for category — minimal: prompt(). For prod-grade UX we'd open a small picker.
    const choice = window.prompt(
      `Wybierz kategorię galerii:\n\n${GALLERY_CATEGORIES.map((c, i) => `${i + 1}. ${c}`).join("\n")}\n\nWpisz numer (1-${GALLERY_CATEGORIES.length}):`,
      "6"
    );
    if (!choice) return;
    const idx = parseInt(choice, 10) - 1;
    const category = GALLERY_CATEGORIES[idx];
    if (!category) return;
    pinMutation.mutate({ pin: true, category });
  };

  const frames = data?.frames ?? [];
  const seq = data?.sequence;
  const active = frames[activeIdx];

  const downloadAll = async (): Promise<void> => {
    for (const f of frames) {
      const link = document.createElement("a");
      link.href = f.signedUrl;
      link.download = `sequence-${sequenceId}-frame-${f.index}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      await new Promise((r) => setTimeout(r, 200));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="sequence-detail-modal-overlay"
    >
      <div
        className="bg-background border border-border rounded-xl w-full max-w-5xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="sequence-detail-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
              Sekwencja Stories
            </div>
            <div className="text-sm font-bold text-foreground truncate">
              {seq?.title ?? "Sekwencja"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {seq && (
              <button
                onClick={() => showcaseMutation.mutate(!(seq.showInShowcase === true))}
                disabled={showcaseMutation.isPending}
                className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-md border transition-colors ${
                  seq.showInShowcase
                    ? "bg-[#ef3e29]/15 hover:bg-[#ef3e29]/25 text-[#ef3e29] border-[#ef3e29]/40"
                    : "bg-muted hover:bg-muted/80 text-muted-foreground border-border"
                } disabled:opacity-50`}
                data-testid="btn-showcase-sequence"
                title={
                  seq.showInShowcase
                    ? "Ukryj z paska w nagłówku Stories"
                    : "Pokaż w pasku w nagłówku Stories"
                }
              >
                {seq.showInShowcase ? <PinOff size={12} /> : <Pin size={12} />}
                {seq.showInShowcase ? "W pasku" : "Pokaż w pasku"}
              </button>
            )}
            {isSuperAdmin && seq && (
              <button
                onClick={handlePinToggle}
                disabled={pinMutation.isPending}
                className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-md border transition-colors ${
                  seq.isInGallery
                    ? "bg-[#ef3e29]/15 hover:bg-[#ef3e29]/25 text-[#ef3e29] border-[#ef3e29]/40"
                    : "bg-muted hover:bg-muted/80 text-muted-foreground border-border"
                } disabled:opacity-50`}
                data-testid="btn-pin-sequence-gallery"
                title={
                  seq.isInGallery
                    ? `Odepnij z galerii (${seq.galleryCategory ?? ""})`
                    : "Przypnij do galerii viral.partners"
                }
              >
                {seq.isInGallery ? <PinOff size={12} /> : <Pin size={12} />}
                {seq.isInGallery ? "W galerii" : "Do galerii"}
              </button>
            )}
            {frames.length > 0 && (
              <button
                onClick={downloadAll}
                className="flex items-center gap-1.5 text-[11px] bg-muted hover:bg-muted/80 text-muted-foreground px-3 py-1.5 rounded-md border border-border"
                data-testid="btn-download-sequence"
              >
                <Download size={12} />
                Pobierz wszystkie
              </button>
            )}
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground p-1"
              data-testid="btn-close-sequence-detail"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <Loader2 className="animate-spin" size={24} />
          </div>
        )}

        {isError && (
          <div className="flex-1 flex items-center justify-center text-red-500 text-sm">
            Błąd ładowania sekwencji.
          </div>
        )}

        {!isLoading && !isError && (
          <div className="flex-1 overflow-y-auto p-5">
            <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-5">
              {/* Active frame preview */}
              <div className="space-y-3">
                <div className="relative w-[280px] aspect-[9/16] mx-auto rounded-xl overflow-hidden border border-border bg-black">
                  {active && (
                    <img
                      src={active.signedUrl}
                      alt={`Frame ${active.index}`}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                  {frames.length > 1 && (
                    <>
                      <button
                        onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
                        disabled={activeIdx === 0}
                        className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full disabled:opacity-30"
                        data-testid="btn-prev-frame"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        onClick={() => setActiveIdx((i) => Math.min(frames.length - 1, i + 1))}
                        disabled={activeIdx === frames.length - 1}
                        className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full disabled:opacity-30"
                        data-testid="btn-next-frame"
                      >
                        <ChevronRight size={16} />
                      </button>
                      <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                        {activeIdx + 1}/{frames.length}
                      </div>
                    </>
                  )}
                </div>

                {/* Thumb strip */}
                <div className="flex gap-1.5 justify-center flex-wrap max-w-[280px] mx-auto">
                  {frames.map((f, i) => (
                    <button
                      key={f.graphicId}
                      onClick={() => setActiveIdx(i)}
                      className={`w-10 aspect-[9/16] rounded overflow-hidden border-2 transition-all ${
                        i === activeIdx
                          ? "border-[#ef3e29]"
                          : "border-border opacity-60 hover:opacity-100"
                      }`}
                      data-testid={`thumb-frame-${i}`}
                    >
                      <img
                        src={f.signedUrl}
                        alt={`Thumb ${f.index}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Metadata */}
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                    Liczba klatek
                  </div>
                  <div className="text-sm text-foreground">{seq?.frameCount}</div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                    Wygenerowano
                  </div>
                  <div className="text-sm text-foreground">
                    {seq?.createdAt ? new Date(seq.createdAt).toLocaleString("pl-PL") : "—"}
                  </div>
                </div>

                {seq?.generationMeta && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                      Konfiguracja
                    </div>
                    <pre className="text-[11px] text-muted-foreground bg-muted border border-border rounded p-2.5 whitespace-pre-wrap break-all">
                      {JSON.stringify(seq.generationMeta, null, 2)}
                    </pre>
                  </div>
                )}

                {seq?.scriptJson && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                      Scenariusz (script)
                    </div>
                    <pre className="text-[10px] text-muted-foreground bg-muted border border-border rounded p-2.5 whitespace-pre-wrap break-all max-h-[260px] overflow-y-auto">
                      {JSON.stringify(seq.scriptJson, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
